import { HeightUnits, WeightUnits } from '@features/Quiz/constants';
import type { TpPilatesQuizStep } from '@features/Quiz/types';
import dayjs from 'dayjs';
import { type Instance, applySnapshot, types } from 'mobx-state-tree';

const SINGLE_STEP_TO_PROPERTY_MAP: Map<string, string> = new Map([
  ['wpWelcome', 'wallPilatesWelcome'],
  ['wpBefore', 'wallPilatesWorkoutsBefore'],
  ['wpDescribeBody', 'wpDescribeBody'],
  ['wpDreamBody', 'wpDreamBody'],
  ['wpBestShape', 'wpBestShape'],
  ['wpTypicalDay', 'wpTypicalDay'],
  ['wpOftenWalks', 'wpOftenWalks'],
  ['wpTouchToes', 'wpTouchToes'],
  ['wpOutOfBreath', 'wpOutOfBreath'],
  ['wpFallbackRoutine', 'wpFallbackRoutine'],
  ['wpDissatisfied', 'wpDissatisfied'],
  ['wpGiveUp', 'wpGiveUp'],
  ['wpSuitableWorkouts', 'wpSuitableWorkouts'],
  ['wpCellulite', 'wpCellulite'],
  ['wpWeightFluctuate', 'wpWeightFluctuate'],
  ['wpIntensityLevel', 'wpIntensityLevel'],
  ['wpTimesPerWeek', 'wpTimesPerWeek'],
  ['wpWorkoutsLong', 'wpWorkoutsLong'],
  ['wpStretching', 'wpStretching'],
  ['wpForwardLunge', 'wpForwardLunge'],
  ['wpWallSit', 'wpWallSit'],
  ['wpBodyWeightSquat', 'wpBodyWeightSquat'],
  ['wpDrinkADay', 'wpDrinkADay'],
  ['wpEnergyLevels', 'wpEnergyLevels'],
  ['wpOnYourPeriod', 'wpOnYourPeriod'],
  ['wpSleepEveryday', 'wpSleepEveryday'],
  ['wpFeelingRested', 'wpFeelingRested'],
  ['wpEatFruits', 'wpEatFruits'],
  ['wpEatingDisorder', 'wpEatingDisorder'],
  ['wpAnyMedication', 'wpAnyMedication'],
  ['wpPregnant', 'wpPregnant'],
  ['wpPhysicalOrMobilityRestrictions', 'wpPhysicalOrMobilityRestrictions'],
  ['wpThroughMenopause', 'wpThroughMenopause'],
  ['wpMotivatedToReach', 'wpMotivatedToReach'],
  ['wpHowQuickly', 'wpHowQuickly'],
  ['swpWelcome', 'swpWelcome'],
  ['taiWelcome', 'taiWelcome'],
  ['jwWelcome', 'jwWelcome'],
  ['jw2Gender', 'jw2Gender'],
  ['jw2Age', 'jw2Age'],
  ['jwPracticedBefore', 'jwPracticedBefore'],
]);

const MULTIPLE_STEP_TO_PROPERTY_MAP: Map<string, string> = new Map([
  ['wpFocusOn', 'wallPilatesFocusOn'],
  ['wpAreasFocusOn', 'wpAreasFocusOn'],
  ['wpActivitiesPart', 'wpActivitiesPart'],
  ['wpComfortAndSafety', 'wpComfortAndSafety'],
  ['wpWhereExercise', 'wpWhereExercise'],
  ['wpImproveSleep', 'wpImproveSleep'],
  ['wpDiet', 'wpDiet'],
  ['wpFoodsCrave', 'wpFoodsCrave'],
  ['wpTheseHabits', 'wpTheseHabits'],
  ['wpHealthDiagnosed', 'wpHealthDiagnosed'],
  ['wpChangeAboutSkin', 'wpChangeAboutSkin'],
  ['wpWantToGetInShape', 'wpWantToGetInShape'],
  ['wpGainedWeight', 'wpGainedWeight'],
  ['wpMotivatesToExercise', 'wpMotivatesToExercise'],
  ['wpHardMotivatedPast', 'wpHardMotivatedPast'],
  ['wpWantToExplore', 'wpWantToExplore'],
]);

type StepData = {
  stepName: string;
  question: string;
  answer: string | string[];
  index?: number;
};

const isSingleOptionStep = (step: TpPilatesQuizStep): boolean =>
  step.type === 'singleOption' || step.type === 'singleOptionMinimal';

const getInitialStepAnswer = (step: TpPilatesQuizStep): string | string[] =>
  isSingleOptionStep(step) ? '' : [];

export const QuizStoreModel = types
  .model('QuizStore')
  .props({
    age: types.maybe(types.string),
    country: types.maybe(types.string),
    countryCode: types.optional(types.string, 'US'),
    city: types.maybe(types.string),
    cheapOfferDeclined: types.maybe(types.boolean),
    goal: types.maybe(types.string),
    targetZone: types.maybe(types.string),
    goalWeightValue: types.maybe(types.number),
    goalWeightUnits: types.maybe(types.enumeration(Object.values(WeightUnits))),
    ageNumber: types.maybe(types.number),
    badHabits: types.maybe(types.frozen(types.array(types.string))),
    currentBMI: types.maybe(types.number),
    energyLevel: types.maybe(types.string),
    flexible: types.maybe(types.string),
    lastScreen: false,
    lifeEvents: types.maybe(types.frozen(types.array(types.string))),
    struggleWith: types.maybe(types.frozen(types.array(types.string))),
    targetZones: types.maybe(types.frozen(types.array(types.string))),
    userName: types.maybe(types.string),
    weightPerDay: types.maybe(types.number),
    whenEvent: types.maybeNull(types.Date),
    heightValue: types.maybe(types.number),
    heightUnits: types.maybe(types.enumeration(Object.values(HeightUnits))),
    weightValue: types.maybe(types.number),
    weightUnits: types.maybe(types.enumeration(Object.values(WeightUnits))),
    // Single option properties from SINGLE_STEP_TO_PROPERTY_MAP
    anyDiet: types.optional(types.string, ''),
    bestShape: types.optional(types.string, ''),
    breakfastTime: types.optional(types.string, ''),
    dailyDrinking: types.optional(types.string, ''),
    dinnerTime: types.optional(types.string, ''),
    dreamBodyPilates: types.optional(types.string, ''),
    energyLevelPilates: types.optional(types.string, ''),
    eventComing: types.optional(types.string, ''),
    exercise: types.optional(types.string, ''),
    fitnessExperience: types.optional(types.string, ''),
    flexiblePilates: types.optional(types.string, ''),
    goalPilates: types.optional(types.string, ''),
    lunchTime: types.optional(types.string, ''),
    outOfBreath: types.optional(types.string, ''),
    physicalBuild: types.optional(types.string, ''),
    preferWorkout: types.optional(types.string, ''),
    triedPilates: types.optional(types.string, ''),
    typicalDay: types.optional(types.string, ''),
    usuallySleep: types.optional(types.string, ''),
    walks: types.optional(types.string, ''),
    agePilates: types.optional(types.string, ''),
    workSchedule: types.optional(types.string, ''),
    newBodyExpect: types.optional(types.string, ''),
    newBodyFeelSexy: types.optional(types.string, ''),
    newBodyIntimacy: types.optional(types.string, ''),
    newBodyMuscles: types.optional(types.string, ''),
    newBodyWorried: types.optional(types.string, ''),
    newBodyWeightChange: types.optional(types.string, ''),
    newBodyBestShape: types.optional(types.string, ''),
    newBodyBreast: types.optional(types.string, ''),
    newBodyBreastShape: types.optional(types.string, ''),
    newBodyFlexible: types.optional(types.string, ''),
    newBodyExercise: types.optional(types.string, ''),
    newBodyStairs: types.optional(types.string, ''),
    newBodyEnergyLevel: types.optional(types.string, ''),
    newBodySexyInBed: types.optional(types.string, ''),
    newBodyHusband: types.optional(types.string, ''),
    newBodyFeelMoreSexy: types.optional(types.string, ''),
    newBodyConnectedBody: types.optional(types.string, ''),
    newBodyReady: types.optional(types.string, ''),
    wallPilatesWelcome: types.optional(types.string, ''),
    wallPilatesWorkoutsBefore: types.optional(types.string, ''),
    wpDescribeBody: types.optional(types.string, ''),
    wpDreamBody: types.optional(types.string, ''),
    wpBestShape: types.optional(types.string, ''),
    wpTypicalDay: types.optional(types.string, ''),
    wpOftenWalks: types.optional(types.string, ''),
    wpTouchToes: types.optional(types.string, ''),
    wpOutOfBreath: types.optional(types.string, ''),
    wpFallbackRoutine: types.optional(types.string, ''),
    wpDissatisfied: types.optional(types.string, ''),
    wpGiveUp: types.optional(types.string, ''),
    wpSuitableWorkouts: types.optional(types.string, ''),
    wpCellulite: types.optional(types.string, ''),
    wpWeightFluctuate: types.optional(types.string, ''),
    wpIntensityLevel: types.optional(types.string, ''),
    wpTimesPerWeek: types.optional(types.string, ''),
    wpWorkoutsLong: types.optional(types.string, ''),
    wpStretching: types.optional(types.string, ''),
    wpForwardLunge: types.optional(types.string, ''),
    wpWallSit: types.optional(types.string, ''),
    wpBodyWeightSquat: types.optional(types.string, ''),
    wpDrinkADay: types.optional(types.string, ''),
    wpEnergyLevels: types.optional(types.string, ''),
    wpOnYourPeriod: types.optional(types.string, ''),
    wpSleepEveryday: types.optional(types.string, ''),
    wpFeelingRested: types.optional(types.string, ''),
    wpEatFruits: types.optional(types.string, ''),
    wpEatingDisorder: types.optional(types.string, ''),
    wpAnyMedication: types.optional(types.string, ''),
    wpPregnant: types.optional(types.string, ''),
    wpPhysicalOrMobilityRestrictions: types.optional(types.string, ''),
    wpThroughMenopause: types.optional(types.string, ''),
    wpMotivatedToReach: types.optional(types.string, ''),
    wpHowQuickly: types.optional(types.string, ''),
    swpWelcome: types.optional(types.string, ''),
    taiWelcome: types.optional(types.string, ''),
    jwWelcome: types.optional(types.string, ''),
    jw2Gender: types.optional(types.string, ''),
    jw2Age: types.optional(types.string, ''),
    jwPracticedBefore: types.optional(types.string, ''),
    // Multiple option properties from MULTIPLE_STEP_TO_PROPERTY_MAP
    wallPilatesFocusOn: types.maybe(types.frozen(types.array(types.string))),
    wpAreasFocusOn: types.maybe(types.frozen(types.array(types.string))),
    wpActivitiesPart: types.maybe(types.frozen(types.array(types.string))),
    wpComfortAndSafety: types.maybe(types.frozen(types.array(types.string))),
    wpWhereExercise: types.maybe(types.frozen(types.array(types.string))),
    wpImproveSleep: types.maybe(types.frozen(types.array(types.string))),
    wpDiet: types.maybe(types.frozen(types.array(types.string))),
    wpFoodsCrave: types.maybe(types.frozen(types.array(types.string))),
    wpTheseHabits: types.maybe(types.frozen(types.array(types.string))),
    wpHealthDiagnosed: types.maybe(types.frozen(types.array(types.string))),
    wpChangeAboutSkin: types.maybe(types.frozen(types.array(types.string))),
    wpWantToGetInShape: types.maybe(types.frozen(types.array(types.string))),
    wpGainedWeight: types.maybe(types.frozen(types.array(types.string))),
    wpMotivatesToExercise: types.maybe(types.frozen(types.array(types.string))),
    wpHardMotivatedPast: types.maybe(types.frozen(types.array(types.string))),
    wpWantToExplore: types.maybe(types.frozen(types.array(types.string))),
    stepToPropertyMap: types.optional(
      types.frozen<Record<string, StepData>>(),
      {},
    ),
  })
  .views((self: any) => ({
    getStepDataByName: (
      stepName: string,
    ): { answer: string | string[]; index?: number; question: string } => {
      const stepData = self.stepToPropertyMap[String(stepName)];
      return stepData
        ? {
            answer: stepData.answer ?? '',
            index: stepData.index,
            question: stepData.question ?? '',
          }
        : { answer: '', index: undefined, question: '' };
    },
    getStepArrayValueByName: (step: string): string[] => {
      const stepData = self.stepToPropertyMap[String(step)];
      if (stepData && Array.isArray(stepData.answer)) {
        return stepData.answer;
      }
      return [];
    },
    getSingleOptionSelectStepValue: (step: string) => {
      const property = SINGLE_STEP_TO_PROPERTY_MAP.get(step);
      if (property) {
        const value = self[property as keyof typeof self];
        return typeof value === 'string' ? value : '';
      }

      const stepData = self.stepToPropertyMap[String(step)];
      if (stepData && typeof stepData.answer === 'string') {
        return stepData.answer;
      }

      return '';
    },
    getMultipleOptionsSelectStepValue: (step: string) => {
      const property = MULTIPLE_STEP_TO_PROPERTY_MAP.get(step);
      if (property) {
        return self[property as keyof typeof self];
      }

      const stepData = self.stepToPropertyMap[String(step)];
      if (stepData && Array.isArray(stepData.answer)) {
        return stepData.answer;
      }

      return [];
    },
  }))
  .views((self: any) => ({
    get goalCompleted() {
      return self.goal != null;
    },
    get targetZoneCompleted() {
      return self.targetZone != null;
    },
    get ageCompleted() {
      return self.age != null;
    },
    get heightCompleted() {
      return self.heightValue != null;
    },
    get currentBMICompleted() {
      return self.currentBMI != null;
    },
    get weightCompleted() {
      return self.weightValue != null;
    },
    get goalWeightCompleted() {
      return self.goalWeightValue != null;
    },
    get flexibleCompleted() {
      return self.flexible != null;
    },
    get anyDietaryCompleted() {
      return self.anyDietary != null;
    },
    get agePilatesCompleted() {
      return self.agePilates != null;
    },
    get energyLevelCompleted() {
      return self.energyLevel != null;
    },
    get whenEventCompleted() {
      return self.whenEvent !== undefined;
    },
    get goalPilatesCompleted() {
      return self.goalPilates != null;
    },
    get physicalBuildCompleted() {
      return self.physicalBuild != null;
    },
    get dreamBodyPilatesCompleted() {
      return self.dreamBodyPilates != null;
    },
    get fitnessExperienceCompleted() {
      return self.fitnessExperience != null;
    },
    get bestShapeCompleted() {
      return self.bestShape != null;
    },
    get triedPilatesCompleted() {
      return self.triedPilates != null;
    },
    get flexiblePilatesCompletes() {
      return self.flexiblePilates != null;
    },
    get exerciseCompleted() {
      return self.exercise != null;
    },
    get outOfBreathCompleted() {
      return self.outOfBreath != null;
    },
    get walksCompleted() {
      return self.walks != null;
    },
    get targetZonesCompleted() {
      return self.targetZones != null && self.targetZones.length > 0;
    },
    get struggleWithCompleted() {
      return self.struggleWith != null && self.struggleWith.length > 0;
    },
    get meaningGoal() {
      switch (self.goal) {
        case 'Weight loss': {
          return 0;
        }
        case 'Bikini body': {
          return 1;
        }
        case 'Postpartum recovery': {
          return 2;
        }
        default: {
          return 0;
        }
      }
    },
    get workScheduleCompleted() {
      return self.workSchedule != null;
    },
    get typicalDayCompleted() {
      return self.typicalDay != null;
    },
    get energyLevelPilatesCompleted() {
      return self.energyLevelPilates != null;
    },
    get usuallySleepCompleted() {
      return self.usuallySleep != null;
    },
    get dailyDrinkingCompleted() {
      return self.dailyDrinking != null;
    },
    get breakfastTimeCompleted() {
      return self.breakfastTime != null;
    },
    get ageNumberCompleted() {
      return self.ageNumber != null;
    },
    get userNameCompleted() {
      return self.userName != null;
    },
    get lunchTimeCompleted() {
      return self.lunchTime != null;
    },
    get dinnerTimeCompleted() {
      return self.dinnerTime != null;
    },
    get anyDietCompleted() {
      return self.anyDiet != null;
    },
    get badHabitsCompleted() {
      return self.badHabits != null && self.badHabits.length > 0;
    },
    get lifeEventsCompleted() {
      return self.lifeEvents != null && self.lifeEvents.length > 0;
    },
    get eventComingCompleted() {
      return self.eventComing != null;
    },
    get preferWorkoutCompleted() {
      return self.preferWorkout != null;
    },
  }))
  .actions((self: any) => ({
    setPreference: ({
      stepName,
      question,
      value,
      index,
    }: {
      stepName: string;
      question: string;
      value: string;
      index?: number;
    }) => {
      self.stepToPropertyMap = {
        ...self.stepToPropertyMap,
        [String(stepName)]: {
          stepName: String(stepName),
          question,
          answer: value,
          index,
        },
      };

      const property = SINGLE_STEP_TO_PROPERTY_MAP.get(String(stepName));
      if (property) {
        self[property as keyof typeof self] = value;
      }
    },

    setPreferences: ({
      stepName,
      question,
      value,
    }: {
      stepName: string;
      question: string;
      value: string[] | undefined | null;
    }) => {
      self.stepToPropertyMap = {
        ...self.stepToPropertyMap,
        [String(stepName)]: {
          stepName: String(stepName),
          question,
          answer: value == null ? [] : value,
        },
      };
    },
    setCheapOfferDeclined: (value: boolean) => {
      self.cheapOfferDeclined = value;
    },
    setCountry: (value: string) => {
      self.country = value;
    },
    setCountryCode: (value: string) => {
      self.countryCode = value;
    },
    setCity: (value: string) => {
      self.city = value;
    },
    setGoal: (value: string) => {
      self.goal = value;
    },
    setTargetZone: (value: string) => {
      self.targetZone = value;
    },
    setAge: (value: string) => {
      self.age = value;
    },
    setHeight: (value: number, units: HeightUnits) => {
      self.heightValue = value;
      self.heightUnits = units;

      if (self.weightUnits == null && self.goalWeightUnits == null) {
        const weightUnits =
          units === HeightUnits.FEET ? WeightUnits.LBS : WeightUnits.KG;
        self.weightUnits = weightUnits;
        self.goalWeightUnits = weightUnits;
      }
    },
    setWeight: (value: number, units: WeightUnits) => {
      self.weightValue = value;
      self.weightUnits = units;
    },
    setGoalWeight: (value: number, units: WeightUnits) => {
      self.goalWeightValue = value;
      self.goalWeightUnits = units;
    },
    setCurrentBMI: (value: number) => {
      self.currentBMI = value;
    },
    setWeightPerDay: (value: number) => {
      self.weightPerDay = value;
    },
    setFlexible: (value: string) => {
      self.flexible = value;
    },
    setLastScreen: (value: boolean) => {
      self.lastScreen = value;
    },
    setAgePilates: (value: string) => {
      self.agePilates = value;
    },
    setEnergyLevel: (value: string) => {
      self.energyLevel = value;
    },
    setWhenEvent: (value: Date | null) => {
      self.whenEvent = value;
    },
    setGoalPilates: (value: string) => {
      self.goalPilates = value;
    },
    setFitnessExperience: (value: string) => {
      self.fitnessExperience = value;
    },
    setBestShape: (value: string) => {
      self.bestShape = value;
    },
    setTriedPilates: (value: string) => {
      self.triedPilates = value;
    },
    setFlexiblePilates: (value: string) => {
      self.flexiblePilates = value;
    },
    setExercise: (value: string) => {
      self.exercise = value;
    },
    setOutOfBreath: (value: string) => {
      self.outOfBreath = value;
    },
    setTargetZones: (value?: readonly string[] | null) => {
      self.targetZones = value == null ? [] : value;
    },
    setStruggleWith: (value?: readonly string[] | null) => {
      self.struggleWith = value == null ? [] : value;
    },
    setWorkSchedule: (value: string) => {
      self.workSchedule = value;
    },
    setBreakfastTime: (value: string) => {
      self.breakfastTime = value;
    },
    setAgeNumber: (value: number) => {
      self.ageNumber = value;
    },
    setUserName: (value: string) => {
      self.userName = value;
    },
    setLunchTime: (value: string) => {
      self.lunchTime = value;
    },
    setAnyDiet: (value: string) => {
      self.anyDiet = value;
    },
    setTypicalDay: (value: string) => {
      self.typicalDay = value;
    },
    setEnergyLevelPilates: (value: string) => {
      self.energyLevelPilates = value;
    },
    setUsuallySleep: (value: string) => {
      self.usuallySleep = value;
    },
    setDailyDrinking: (value: string) => {
      self.dailyDrinking = value;
    },
    setBadHabits: (value?: readonly string[] | null) => {
      self.badHabits = value == null ? [] : value;
    },
    setLifeEvents: (value?: readonly string[] | null) => {
      self.lifeEvents = value == null ? [] : value;
    },
    setEventComing: (value: string) => {
      self.eventComing = value;
    },
    setPreferWorkout: (value: string) => {
      self.preferWorkout = value;
    },

    fillInAllSteps: () => {
      if (!self.goalCompleted) {
        self.goal = 'Weight loss';
      }
      if (!self.targetZoneCompleted) {
        self.targetZone = 'toned_butt';
      }
      if (!self.ageCompleted) {
        self.age = '18_29';
      }
      if (!self.currentBMICompleted) {
        self.currentBMI = 25;
      }
      if (!self.heightCompleted) {
        self.heightValue = 170;
        self.heightUnits = HeightUnits.CM;
      }
      if (!self.weightCompleted) {
        self.weightValue = 80;
        self.weightUnits = WeightUnits.KG;
      }
      if (!self.goalWeightCompleted) {
        self.goalWeightValue = 65;
        self.goalWeightUnits = WeightUnits.KG;
      }
      if (!self.flexibleCompleted) {
        self.flexible = 'pretty_flexible';
      }
      if (!self.anyDietaryCompleted) {
        self.anyDietary = ['low_carb_or_keto'];
      }
      if (!self.agePilatesCompleted) {
        self.agePilates = '18_35';
      }
      if (!self.energyLevelCompleted) {
        self.energyLevel = "it's_fine_and_stable";
      }
      if (!self.whenEventCompleted) {
        self.whenEvent = dayjs().toDate();
      }
      if (!self.goalPilatesCompleted) {
        self.goalPilates = 'lose_weight';
      }
      if (!self.physicalBuildCompleted) {
        self.physicalBuild = 'slim';
      }
      if (!self.dreamBodyPilatesCompleted) {
        self.dreamBodyPilates = 'thin';
      }
      if (!self.fitnessExperienceCompleted) {
        self.fitnessExperience = 'trouble_gaining_muscle';
      }
      if (!self.bestShapeCompleted) {
        self.bestShape = 'less_than_a_year_ago';
      }
      if (!self.triedPilatesCompleted) {
        self.triedPilates = 'regularly';
      }
      if (!self.flexiblePilatesCompletes) {
        self.flexiblePilates = 'pretty_flexible';
      }
      if (!self.exerciseCompleted) {
        self.exercise = 'almost_every_day';
      }
      if (!self.outOfBreathCompleted) {
        self.outOfBreath = 'can_not_talk';
      }
      if (!self.walksCompleted) {
        self.walks = 'almost_every_day';
      }
      if (!self.targetZonesCompleted) {
        self.targetZones = ['flat_belly'];
      }
      if (!self.struggleWithCompleted) {
        self.struggleWith = ['sensitive_back'];
      }
      if (!self.workScheduleCompleted) {
        self.workSchedule = '9_to_5';
      }
      if (!self.breakfastTimeCompleted) {
        self.breakfastTime = 'between_6_and_8_am';
      }
      if (!self.ageNumberCompleted) {
        self.ageNumber = 30;
      }
      if (!self.userNameCompleted) {
        self.userName = 'Vadim';
      }
      if (!self.lunchTimeCompleted) {
        self.lunchTime = 'between_10_am_and_noon';
      }
      if (!self.dinnerTimeCompleted) {
        self.dinnerTime = 'between_4_and_6_pm';
      }
      if (!self.anyDietCompleted) {
        self.anyDiet = 'keto';
      }
      if (!self.typicalDayCompleted) {
        self.typicalDay = 'sitting';
      }
      if (!self.energyLevelPilatesCompleted) {
        self.energyLevelPilates = 'balanced_and_steady';
      }
      if (!self.usuallySleepCompleted) {
        self.usuallySleep = 'less_than_5_hours';
      }
      if (!self.dailyDrinkingCompleted) {
        self.dailyDrinking = 'only_coffee_or_tea';
      }
      if (!self.badHabitsCompleted) {
        self.badHabits = ['eat_late_at_night'];
      }
      if (!self.lifeEventsCompleted) {
        self.lifeEvents = ['marriage_or_relationship'];
      }
      if (!self.eventComingCompleted) {
        self.eventComing = 'Wedding';
      }
      if (!self.preferWorkoutCompleted) {
        self.preferWorkout = 'afternoon';
      }
    },
  }))
  .actions((self: any) => ({
    reset: () => {
      applySnapshot(self, {});
    },

    updateStepToPropertyMap: (steps: Record<string, TpPilatesQuizStep>) => {
      console.log('RUN updateStepToPropertyMap');
      const newMap = { ...self.stepToPropertyMap };

      for (const [_, step] of Object.entries(steps)) {
        if (step.stepName !== undefined) {
          const stepName = String(step.stepName);
          if (!(stepName in newMap)) {
            newMap[stepName] = {
              stepName,
              question: '',
              answer: getInitialStepAnswer(step),
            };
          } else if (
            isSingleOptionStep(step) &&
            Array.isArray(newMap[stepName].answer) &&
            newMap[stepName].answer.length === 0
          ) {
            newMap[stepName] = {
              ...newMap[stepName],
              answer: '',
            };
          } else if (
            !isSingleOptionStep(step) &&
            typeof newMap[stepName].answer === 'string' &&
            newMap[stepName].answer.length === 0
          ) {
            newMap[stepName] = {
              ...newMap[stepName],
              answer: [],
            };
          }
        }
      }

      self.stepToPropertyMap = newMap;
    },
  }));

type QuizStoreType = Instance<typeof QuizStoreModel>;

export interface QuizStore extends QuizStoreType {}
